// ---------------------------------------------------------------------------
// DnD Email Editor – HTML block normalizer
//
// Users paste whole HTML documents (DOCTYPE, <head>, <style>, the lot) into an
// html block. That breaks two things: an email block can only hold body
// content, and a <style> block injected by the canvas restyles the whole app
// (the canvas shares the app's document — see dnd-sanitize).
//
// findBlockIssues() reports what makes a block unsaveable; normalizeHtmlBlock()
// fixes it by lifting the body content out and inlining the stylesheet onto the
// elements it matched — the same precedence model juice / Emogrifier use:
// (specificity, source order) ascending, with any pre-existing inline style
// winning last.
//
// What cannot be inlined is dropped and reported, never silently: @media and
// @font-face blocks, and pseudo-selectors like :hover, have no inline
// equivalent. (The compiler emits its own responsive @media block, so a pasted
// one is usually redundant anyway.)
//
// ::before / ::after are the exception. Dropping them loses content, not just
// styling — a counter-based numbered list
//
//   ol { counter-reset: step; list-style: none }
//   li { counter-increment: step }
//   li::before { content: counter(step) ". " }
//
// would inline the counter properties (they are ordinary properties) and drop
// the rule that actually prints the number, leaving a list with no markers at
// all. So generated content is resolved and baked into the markup as a <span>:
// counters are evaluated in document order, and the result works in every
// client — email clients don't support CSS counters or ::before regardless of
// what this function does with them.
// ---------------------------------------------------------------------------

/** Document-level tags that must not survive in a block. */
const REMOVABLE_TAGS = ['style', 'link', 'script', 'meta', 'base', 'title'] as const;

type RemovableTag = (typeof REMOVABLE_TAGS)[number];

const DOC_SHELL_RE = /<\s*(?:!doctype|html|head|body)\b/i;

export interface BlockIssue {
  kind: 'document-shell' | RemovableTag;
  /** Human-readable phrase for the banner, e.g. "2 <style> tags". */
  label: string;
}

/** Comments are inert (an mso conditional's <style> never renders), so a
 *  working copy without them is what the document-shell test looks at. */
function withoutComments(html: string): string {
  return html.replace(/<!--[\s\S]*?-->/g, '');
}

/**
 * What disqualifies this html from being a block. Empty array = saveable.
 * Detection runs against the parsed DOM (not the raw string) so markup that
 * only appears inside a comment is not flagged — otherwise a block carrying an
 * Outlook conditional could never be saved.
 */
export function findBlockIssues(html: string): BlockIssue[] {
  if (!html?.trim() || typeof DOMParser === 'undefined') return [];
  const issues: BlockIssue[] = [];

  if (DOC_SHELL_RE.test(withoutComments(html))) {
    issues.push({ kind: 'document-shell', label: 'a full document wrapper (<html>/<head>/<body>)' });
  }

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    for (const tag of REMOVABLE_TAGS) {
      const count = doc.querySelectorAll(tag).length;
      if (count > 0) {
        issues.push({ kind: tag, label: `${count} <${tag}> tag${count === 1 ? '' : 's'}` });
      }
    }
  } catch {
    // Unparseable: nothing reliable to report, let the user save as-is.
  }

  return issues;
}

export interface NormalizeResult {
  html: string;
  /** Selectors whose declarations were inlined onto at least one element. */
  inlinedSelectors: number;
  /** Rules that have no inline equivalent, e.g. "@media screen", "a:hover". */
  droppedRules: string[];
  /** Document-level tags removed, e.g. "1 <style>". */
  removed: string[];
  /** ::before / ::after markers (list numbers, prefixes) written into the markup. */
  bakedMarkers: number;
}

/**
 * Reduce a pasted document to block content with its CSS inlined.
 * Lossy by design: the stylesheet and document wrapper are gone afterwards.
 */
export function normalizeHtmlBlock(html: string): NormalizeResult {
  const result: NormalizeResult = { html, inlinedSelectors: 0, droppedRules: [], removed: [], bakedMarkers: 0 };
  if (!html?.trim() || typeof DOMParser === 'undefined') return result;

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return result;
  }
  const body = doc.body;
  if (!body) return result;

  // Collect the CSS before the <style> tags are removed.
  let css = '';
  doc.querySelectorAll('style').forEach((node) => { css += `\n${node.textContent ?? ''}`; });

  for (const tag of REMOVABLE_TAGS) {
    const nodes = doc.querySelectorAll(tag);
    if (nodes.length > 0) result.removed.push(`${nodes.length} <${tag}>`);
    nodes.forEach((node) => node.remove());
  }

  if (css.trim()) {
    const { inlinedSelectors, droppedRules, generatedRules } = inlineCss(doc, css);
    result.inlinedSelectors = inlinedSelectors;
    result.droppedRules = droppedRules;

    // Generated content is resolved after inlining, so the counter walk can read
    // counter-reset / counter-increment from the inline styles just written.
    const { baked, unresolved } = bakeGeneratedContent(doc, generatedRules);
    result.bakedMarkers = baked;
    result.droppedRules.push(...unresolved);
  }

  result.html = body.innerHTML;
  return result;
}

// ---- CSS inlining ---------------------------------------------------------

type Declaration = [property: string, value: string, priority: string];

interface CollectedRule {
  specificity: number;
  order: number;
  declarations: Declaration[];
}

/**
 * Let the browser's own CSS parser do the parsing — no regex CSS grammar.
 *
 * `cssRules` is only populated for a <style> inside a live document (a detached
 * `createHTMLDocument()` yields a null sheet in both Chrome and jsdom), so the
 * element is attached to the real document just long enough to read the rules.
 * `media="not all"` means the CSS never applies while it is there, so this does
 * not reintroduce the very leak the caller is cleaning up, and it is removed
 * synchronously in the same tick — nothing can paint in between.
 */
function withParsedRules<T>(css: string, read: (rules: CSSRule[]) => T, fallback: T): T {
  if (typeof document === 'undefined') return fallback;
  const styleEl = document.createElement('style');
  styleEl.media = 'not all';
  styleEl.textContent = css;
  try {
    document.head.appendChild(styleEl);
    const sheet = styleEl.sheet as CSSStyleSheet | null;
    if (!sheet) return fallback;
    return read(Array.from(sheet.cssRules));
  } catch {
    return fallback;
  } finally {
    styleEl.remove();
  }
}

/** CSSRule.STYLE_RULE — a plain `selector { … }` rule. */
const STYLE_RULE = 1;

function isStyleRule(rule: CSSRule): rule is CSSStyleRule {
  // `type` is deprecated but universally present, and it is the only reliable
  // discriminator here: jsdom hands every rule a (usually empty) cssRules list,
  // so "has no nested rules" would classify every style rule as an at-rule.
  if (typeof rule.type === 'number') return rule.type === STYLE_RULE;
  const candidate = rule as CSSStyleRule;
  return typeof candidate.selectorText === 'string' && !!candidate.style;
}

function describeRule(rule: CSSRule): string {
  const grouping = rule as CSSRule & { conditionText?: string; name?: string };
  const head = rule.cssText.split('{')[0]?.trim();
  if (grouping.conditionText) return `@media ${grouping.conditionText}`;
  return head || rule.cssText.slice(0, 60);
}

function readDeclarations(style: CSSStyleDeclaration): Declaration[] {
  const out: Declaration[] = [];
  for (let i = 0; i < style.length; i++) {
    const prop = style.item(i);
    if (!prop) continue;
    out.push([prop, style.getPropertyValue(prop), style.getPropertyPriority(prop)]);
  }
  return out;
}

/** Bracketed sections hold colons that are not pseudo-selectors ([href^="http:"]). */
function withoutBrackets(selector: string): string {
  return selector.replace(/\[[^\]]*\]/g, '[]');
}

/** Pseudo-classes / pseudo-elements have no inline equivalent. */
function isInlinable(selector: string): boolean {
  return !withoutBrackets(selector).includes(':');
}

/** Approximate CSS specificity — the standard a,b,c weighting flattened. */
function specificity(selector: string): number {
  const s = withoutBrackets(selector);
  const ids = (s.match(/#[\w-]+/g) ?? []).length;
  const classes =
    (s.match(/\.[\w-]+/g) ?? []).length +
    (s.match(/\[\]/g) ?? []).length;
  const types = (s.match(/(?:^|[\s>+~])[a-z][\w-]*/gi) ?? []).length;
  return ids * 10000 + classes * 100 + types;
}

function inlineCss(doc: Document, css: string): {
  inlinedSelectors: number;
  droppedRules: string[];
  generatedRules: GeneratedRule[];
} {
  const droppedRules: string[] = [];
  const generatedRules: GeneratedRule[] = [];
  let inlinedSelectors = 0;

  const rules = withParsedRules(css, (r) => r, [] as CSSRule[]);
  if (rules.length === 0) return { inlinedSelectors, droppedRules, generatedRules };

  const collected = new Map<Element, CollectedRule[]>();
  let order = 0;

  for (const rule of rules) {
    order += 1;
    if (!isStyleRule(rule)) {
      droppedRules.push(describeRule(rule));
      continue;
    }
    const declarations = readDeclarations(rule.style);

    for (const raw of rule.selectorText.split(',')) {
      const selector = raw.trim();
      if (!selector) continue;

      // ::before / ::after carry content, so they are resolved into the markup
      // rather than dropped (see the module comment). Checked before the
      // empty-declaration guard below: an engine that refuses to parse the
      // content value (jsdom drops `content: attr(…)`) leaves the rule with no
      // declarations, and it must still be reported rather than vanish.
      const pseudo = matchGeneratedContent(selector);
      if (pseudo) {
        const parts = parseContentValue(rule.style.getPropertyValue('content'));
        if (parts) {
          generatedRules.push({
            selector,
            base: pseudo.base,
            position: pseudo.position,
            parts,
            declarations: declarations.filter(([prop]) => !GENERATED_ONLY_PROPS.has(prop)),
          });
        } else {
          droppedRules.push(selector);
        }
        continue;
      }

      if (declarations.length === 0) continue;

      if (!isInlinable(selector)) {
        droppedRules.push(selector);
        continue;
      }
      let matches: Element[];
      try {
        matches = Array.from(doc.querySelectorAll(selector));
      } catch {
        droppedRules.push(selector);
        continue;
      }
      if (matches.length === 0) continue;
      inlinedSelectors += 1;
      const entry: CollectedRule = { specificity: specificity(selector), order, declarations };
      for (const el of matches) {
        const list = collected.get(el);
        if (list) list.push(entry);
        else collected.set(el, [entry]);
      }
    }
  }

  for (const [el, entries] of collected) {
    entries.sort((a, b) => a.specificity - b.specificity || a.order - b.order);
    const merged = new Map<string, string>();
    for (const entry of entries) {
      for (const [prop, value, priority] of entry.declarations) {
        merged.set(prop, priority ? `${prop}: ${value} !${priority}` : `${prop}: ${value}`);
      }
    }
    // The element's own style attribute is appended last so it wins, matching
    // how the browser resolved it before inlining.
    const own = el.getAttribute('style')?.trim().replace(/;$/, '') ?? '';
    const next = [...merged.values(), own].filter(Boolean).join('; ');
    if (next) el.setAttribute('style', `${next};`);
  }

  return { inlinedSelectors, droppedRules, generatedRules };
}

// ---- Generated content (::before / ::after) -------------------------------

type ContentPart =
  | { kind: 'text'; value: string }
  | { kind: 'counter'; name: string };

interface GeneratedRule {
  /** Original selector, for reporting when it can't be resolved. */
  selector: string;
  /** Selector without the pseudo-element part. */
  base: string;
  position: 'before' | 'after';
  parts: ContentPart[];
  declarations: Declaration[];
}

/** Properties that only make sense on the pseudo-element itself. Positioning is
 *  dropped because the span sits in the text flow, where `position: absolute;
 *  left: -1.5em` would push the marker off the block instead of beside it. */
const GENERATED_ONLY_PROPS = new Set([
  'content',
  'counter-reset',
  'counter-increment',
  'position',
  'top',
  'right',
  'bottom',
  'left',
  'float',
]);

/** `li::before` / `li:before` → { base: 'li', position: 'before' }. */
function matchGeneratedContent(selector: string): { base: string; position: 'before' | 'after' } | null {
  const match = /^(.*?)::?(before|after)$/i.exec(selector.trim());
  if (!match) return null;
  const base = match[1].trim();
  // A base that still holds a pseudo-class (a:hover::before) cannot be resolved
  // to elements we may safely mutate.
  if (!base || !isInlinable(base)) return null;
  return { base, position: match[2].toLowerCase() as 'before' | 'after' };
}

/**
 * Parse a `content` value into literal text and counter references.
 * Returns null for anything not reducible to static text — attr(), url(),
 * open-quote, or an empty/none value — so the caller reports it as dropped.
 */
function parseContentValue(content: string): ContentPart[] | null {
  const raw = content.trim();
  if (!raw || raw === 'none' || raw === 'normal') return null;

  const token = /"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)'|counters?\(\s*([\w-]+)[^)]*\)/g;
  const parts: ContentPart[] = [];
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = token.exec(raw)) !== null) {
    // Anything between tokens that isn't whitespace is a function or keyword we
    // cannot resolve (attr(), url(), open-quote…).
    if (raw.slice(cursor, match.index).trim()) return null;
    cursor = token.lastIndex;
    if (match[1] !== undefined) parts.push({ kind: 'text', value: match[1] });
    else if (match[2] !== undefined) parts.push({ kind: 'text', value: match[2] });
    else parts.push({ kind: 'counter', name: match[3] });
  }
  if (raw.slice(cursor).trim()) return null;

  return parts.length > 0 ? parts : null;
}

/** Parse `counter-reset: step` / `counter-increment: step 2` into name→number. */
function parseCounterOps(value: string, fallback: number): Array<[string, number]> {
  const tokens = value.trim().split(/\s+/).filter(Boolean);
  const ops: Array<[string, number]> = [];
  for (let i = 0; i < tokens.length; i += 1) {
    const name = tokens[i];
    if (!name || name === 'none') continue;
    const next = Number(tokens[i + 1]);
    if (Number.isFinite(next)) {
      ops.push([name, next]);
      i += 1;
    } else {
      ops.push([name, fallback]);
    }
  }
  return ops;
}

/**
 * Resolve each ::before / ::after rule to a <span> in the markup.
 *
 * Counters are evaluated with a single flat scope walked in document order,
 * which is exactly right for a flat list. Nested lists using `counters(name,
 * '.')` get the innermost number only, not the full "1.2" path.
 */
function bakeGeneratedContent(
  doc: Document,
  rules: GeneratedRule[],
): { baked: number; unresolved: string[] } {
  const unresolved: string[] = [];
  if (rules.length === 0) return { baked: 0, unresolved };

  // Which elements each rule applies to.
  const perRule = new Map<GeneratedRule, Element[]>();
  const targets = new Set<Element>();
  for (const rule of rules) {
    let matches: Element[];
    try {
      matches = Array.from(doc.querySelectorAll(rule.base));
    } catch {
      unresolved.push(rule.selector);
      continue;
    }
    if (matches.length === 0) continue;
    perRule.set(rule, matches);
    for (const el of matches) targets.add(el);
  }

  // One document-order walk, snapshotting counter values at each target.
  const counters = new Map<string, number>();
  const snapshots = new Map<Element, Map<string, number>>();
  for (const el of Array.from(doc.body.querySelectorAll<HTMLElement>('*'))) {
    const reset = el.style?.getPropertyValue('counter-reset');
    if (reset) for (const [name, n] of parseCounterOps(reset, 0)) counters.set(name, n);
    const inc = el.style?.getPropertyValue('counter-increment');
    if (inc) for (const [name, n] of parseCounterOps(inc, 1)) counters.set(name, (counters.get(name) ?? 0) + n);
    if (targets.has(el)) snapshots.set(el, new Map(counters));
  }

  let baked = 0;
  for (const [rule, elements] of perRule) {
    const style = rule.declarations
      .map(([prop, value, priority]) => (priority ? `${prop}: ${value} !${priority}` : `${prop}: ${value}`))
      .join('; ');

    for (const el of elements) {
      const counterValues = snapshots.get(el);
      const text = rule.parts
        .map((part) => (part.kind === 'text' ? part.value : String(counterValues?.get(part.name) ?? 0)))
        .join('');
      if (!text) continue;

      const span = doc.createElement('span');
      if (style) span.setAttribute('style', `${style};`);
      span.textContent = text;
      if (rule.position === 'before') el.insertBefore(span, el.firstChild);
      else el.appendChild(span);
      baked += 1;
    }
  }

  // The counter properties are inert once the markers are literal text, and no
  // email client acts on them anyway.
  doc.body.querySelectorAll<HTMLElement>('[style]').forEach((el) => {
    el.style.removeProperty('counter-reset');
    el.style.removeProperty('counter-increment');
    if (!el.getAttribute('style')?.trim()) el.removeAttribute('style');
  });

  return { baked, unresolved };
}
